// The navigation, as data — one source of truth for the desktop sidebar, the
// phone tab bar, and the More page (DM1, phase 8).
//
// The rule underneath all three renderings is unchanged from phase 7:
// navigation is filtered by role rather than rendered and disabled, so a field
// worker never sees a Clients tab at all (spec: 04-clients.md). A hidden link
// is not an access control and is not treated as one — the server refuses
// these routes independently, and a field worker's refusal is a 404.
import type { ReactNode } from 'react';
import { RESOURCE_GROUPS } from '../lib/resourceGroups';

export const ROLE_LABEL = {
  admin: 'Admin',
  manager: 'Manager',
  field: 'Field worker',
} as const;

export type Role = keyof typeof ROLE_LABEL;

export interface NavItem {
  to: string;
  label: string;
  /**
   * A shorter label for the phone tab bar, where six tabs share 375px and a
   * two-word label wraps (round 44: 'My timesheet' became 'Timesheet').
   */
  tabLabel?: string;
  icon: ReactNode;
  end?: boolean;
  /** Which roles see it. Absent means everyone. */
  roles?: Role[];
  /**
   * A nested list, rendered indented under the parent in the desktop sidebar
   * (parity pass 2 batch 1). Only Resources has one: six kinds is more than a
   * single list can carry, and the five groups are the doors into it.
   *
   * Children are navigation only. They are NOT an access control and are not
   * treated as one — they inherit the parent's `roles`, and the server refuses
   * each route independently.
   */
  children?: { to: string; label: string; roles?: Role[]; end?: boolean }[];
}

const icon = (path: ReactNode) => (
  <svg
    className="h-5 w-5 flex-none"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
    aria-hidden="true"
  >
    {path}
  </svg>
);

/**
 * The main navigation, in the order Nathan asked for: home, clients, jobs,
 * resources, schedule, library — then Notifications, which is rendered
 * separately because it carries the unread badge.
 *
 * Home is two different pages behind one link — a manager's dashboard and a
 * field worker's own schedule (see App.tsx). The label changes to match,
 * because "Home" tells a bloke on a site nothing.
 */
/**
 * Company switches that add or remove whole sections. Timesheets ship dark
 * (round 44): a company that has not switched them on sees no entry. Quotes
 * had the same switch until round 45, when Nathan made them always on.
 */
export interface NavFeatures {
  timesheets?: boolean;
}

export function mainNav(role: Role, features: NavFeatures = {}): NavItem[] {
  const isField = role === 'field';

  return [
    {
      to: '/app',
      end: true,
      label: isField ? 'My schedule' : 'Home',
      icon: icon(
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      ),
    },
    {
      // A field worker's own licences and inductions (round 27). Field-only:
      // a manager reads anybody's from the People list, including their own,
      // and a second door to one person's file would be a second thing to
      // keep in step.
      to: '/app/my-record',
      label: 'My record',
      roles: ['field'],
      icon: icon(
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      ),
    },
    {
      to: '/app/clients',
      label: 'Clients',
      roles: ['admin', 'manager'],
      icon: icon(
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
        />
      ),
    },
    // Round 43. Between Clients and Jobs, the order the work happens in: a
    // client asks, a quote prices it, a won quote becomes a job. On for every
    // company since round 45.
    {
      to: '/app/quotes',
      label: 'Quotes',
      roles: ['admin', 'manager'] as Role[],
      icon: icon(
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
      ),
    },
    {
      to: '/app/jobs',
      label: 'Jobs',
      roles: ['admin', 'manager'],
      icon: icon(
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      ),
    },
    {
      to: '/app/resources',
      label: 'Resources',
      roles: ['admin', 'manager'],
      // Just the five kind groups. The two lookup lists that used to hang here
      // (competency codes, machine types) moved to the Settings tabs in round
      // 30 — Nathan's call, reversing rounds 27/28's placement to shorten the
      // sidebar. Their manager-level ACCESS is unchanged; that half of round
      // 27 was about who maintains the lists, not where they sit.
      children: RESOURCE_GROUPS.map((group) => ({
        to: `/app/resources/${group.path}`,
        label: group.label,
      })),
      icon: icon(
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
        />
      ),
    },
    {
      to: '/app/calendar',
      label: 'Schedule',
      roles: ['admin', 'manager'],
      icon: icon(
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      ),
    },
    // Round 44. Timesheets, for EVERY role while the company uses them: a
    // field worker fills in their own week here, and the office reviews
    // everyone's. The label says which it is. Its own world in the sidebar,
    // after the schedule it is reconciled against.
    ...(features.timesheets
      ? [
          {
            to: '/app/timesheets',
            label: isField ? 'My timesheet' : 'Timesheets',
            tabLabel: 'Timesheet',
            icon: icon(
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            ),
          },
        ]
      : []),
    {
      // Forms sits between the schedule and the library, and is open to
      // EVERY role: filling one in is a field worker's job (round 16). The
      // Templates child is where a manager builds them, and it is hidden from
      // workers — as navigation, not as an access control; the server refuses
      // that route independently.
      //
      // Round 28: the landing at /app/forms IS "Complete a form" — the forms
      // themselves, one tap from the phone tab. Round 37 gives it a child of
      // its own SAYING SO (Zanetto Tools' three sub-sections, Nathan's call):
      // the parent row is still the link, but a group whose children were
      // Submissions and Templates read as though filling one in lived
      // somewhere else. `end` so it does not stay lit on its two siblings,
      // which share its path prefix.
      to: '/app/forms',
      label: 'Forms',
      end: true,
      children: [
        { to: '/app/forms', label: 'Complete a form', end: true },
        { to: '/app/forms/submissions', label: 'Submissions' },
        { to: '/app/forms/templates', label: 'Form templates', roles: ['admin', 'manager'] },
      ],
      icon: icon(
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      ),
    },
    {
      // Everyone reads the Library — it is where the fatigue policy lives when
      // somebody on site asks for it.
      to: '/app/library',
      label: 'Library',
      icon: icon(
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        />
      ),
    },
  ];
}

/** One tab on the Settings page. */
export interface SettingsSection {
  to: string;
  label: string;
  /** Which roles see the tab. Absent means everyone. */
  roles?: Role[];
}

/**
 * The Settings page's tabs, in one place (round 30) — the same one-config
 * pattern as PERSON_SECTIONS, and for the same reason: the tab strip, the
 * sidebar's active state and the index redirect all read this list, so they
 * cannot drift.
 *
 * These replaced four loose sidebar links plus the two lookup lists under
 * Resources (Nathan's call, round 30 — a shorter sidebar). The tabs are
 * navigation, not an access control: every route keeps its own guard, and the
 * server refuses each independently.
 *
 * Order: the admin's everyday tabs first, Security LAST because it is the one
 * tab every role has — a field worker's Settings page is just Security, and
 * the index redirect sends each role to the first tab it can see.
 */
export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  // The company's letterhead — what appears at the top of a form PDF — and,
  // since round 30, the company's timezone.
  { to: '/app/settings/company', label: 'Company details', roles: ['admin'] },
  // User accounts. "Users", not "People" (round 30): People already means the
  // person-kind RESOURCES under Resources, and one word carrying both ideas
  // was a genuine ambiguity, not just clutter.
  { to: '/app/settings/users', label: 'Users', roles: ['admin'] },
  // Four billing/export emails deep-link to this URL; it must never move.
  { to: '/app/settings/billing', label: 'Billing', roles: ['admin'] },
  // Round 43. Admin only: the quote numbering and the GST position (the
  // on/off switch went in round 45; quotes are always on).
  { to: '/app/settings/quotes', label: 'Quotes', roles: ['admin'] },
  // Round 44, on the same terms: the switch, the week's first day, the leave
  // and allowance lists, and who fills in a timesheet.
  { to: '/app/settings/timesheets', label: 'Timesheets', roles: ['admin'] },
  // Manager-level, deliberately: whoever runs the crew is who knows what the
  // tickets are called (round 27), and the same for the machine types behind
  // the forms' filter (round 28).
  { to: '/app/settings/competency-codes', label: 'Competency codes', roles: ['admin', 'manager'] },
  // The standard-inductions list (round 37) — competency codes' twin.
  { to: '/app/settings/induction-codes', label: 'Inductions', roles: ['admin', 'manager'] },
  { to: '/app/settings/machine-types', label: 'Machine types', roles: ['admin', 'manager'] },
  // The recycle bin: what the delete confirm's "an admin can get it back"
  // promise points at. Admin-only because it spans the whole tenant.
  { to: '/app/settings/deleted', label: 'Recently deleted', roles: ['admin'] },
  // EVERY role: a field worker has a password to change, and spec 11 offers
  // two-factor to everyone while requiring it only of admins.
  { to: '/app/settings/security', label: 'Security' },
];

/** The tabs a role actually sees. */
export function settingsSections(role: Role): SettingsSection[] {
  return SETTINGS_SECTIONS.filter((s) => !s.roles || s.roles.includes(role));
}

/**
 * The single Settings entry in the sidebar and on the More page. Everyone
 * gets it, because everyone has at least Security behind it.
 */
export const SETTINGS_NAV: NavItem = {
  to: '/app/settings',
  label: 'Settings',
  icon: icon(
    <>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </>
  ),
};

/**
 * The owner console (round 32). Its own entry rather than a Settings tab, and
 * that is a statement rather than a layout preference: Settings is a COMPANY's
 * settings, and this screen is not about the company the person is signed in
 * to. It is drawn only for the one account holding `isPlatformAdmin`, and
 * drawing it is not what admits them — the server checks the same flag and a
 * second factor besides.
 */
export const PLATFORM_NAV: NavItem = {
  to: '/app/platform',
  label: 'Owner console',
  icon: icon(
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6"
    />
  ),
};

export const BELL_ICON = icon(
  <path
    strokeLinecap="round"
    strokeLinejoin="round"
    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
  />
);

/**
 * The system feed (round 37). NOT an item in `mainNav`, deliberately: the two
 * feeds render as an adjacent pair after the main list, each carrying a badge
 * the plain rendering has no slot for. But it IS a destination the phone has
 * to offer somewhere — the tab bar's fixed slot went to Announcements (most
 * phone users are field workers, who cannot see this feed at all), so More is
 * a manager's only door. For round 37's first two months nothing put it
 * there, and on a phone the feed was unreachable (found by Nathan on his own
 * phone, 2026-09-14).
 *
 * Manager+ — the same gate the sidebar entry, the route and the badge poll
 * hold. The sidebar and the More page both read THIS object, so the two
 * renderings cannot drift.
 */
export const NOTIFICATIONS_NAV: NavItem = {
  to: '/app/notifications',
  label: 'Notifications',
  icon: BELL_ICON,
  roles: ['admin', 'manager'],
};

/**
 * Announcements (round 37). A megaphone against the bell, because the two
 * feeds beside each other need to be told apart at a glance: somebody said
 * this, against the software noticed this.
 */
export const MEGAPHONE_ICON = icon(
  <path
    strokeLinecap="round"
    strokeLinejoin="round"
    d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
  />
);

/**
 * The guides, which live on the MARKETING site (round 38).
 *
 * Absolute, and to the bare domain, both deliberately. nginx serves the app on
 * `www` and the static marketing site on `dayboard.com.au`, and the app answers
 * every unknown path with the SPA fallback — so a relative `/guides/` from
 * inside the app is swallowed and lands the reader back on Home. Hardcoded
 * because it is the same address from staging, from a laptop and from a phone:
 * there is one guides site and it is not per-environment.
 */
export const GUIDES_URL = 'https://dayboard.com.au/guides/';

/** An open book. The one entry in the sidebar that leaves the app. */
export const GUIDES_ICON = icon(
  <path
    strokeLinecap="round"
    strokeLinejoin="round"
    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
  />
);

export const MORE_ICON = icon(
  <path
    strokeLinecap="round"
    strokeLinejoin="round"
    d="M5 12h.01M12 12h.01M19 12h.01M5.5 12a.5.5 0 11-1 0 .5.5 0 011 0zm7 0a.5.5 0 11-1 0 .5.5 0 011 0zm7 0a.5.5 0 11-1 0 .5.5 0 011 0z"
  />
);

/**
 * DM1 (phase 8, Nathan's call 2026-08-21): phones get bottom tabs, the
 * one-handed pattern from the Zanetto Tools mobile plan. The bar fits four
 * destinations plus More; these are the four, per role. A field worker's
 * product only HAS three destinations, so their bar is complete without
 * spilling anything into More.
 */
export function phoneTabTargets(role: Role, features: NavFeatures = {}): string[] {
  // Round 44: a company with timesheets on gives a field worker a FOURTH tab,
  // because filling in the week is done on the phone, every week. Library
  // stays on the bar: the SWMS are there when somebody on site asks.
  if (role === 'field' && features.timesheets) {
    return ['/app', '/app/timesheets', '/app/forms', '/app/library'];
  }
  return role === 'field'
    ? // A worker's product is now four destinations, not three: filling in a
      // prestart is a thing they do on a phone, standing at the machine.
      // My record is deliberately NOT one of them — it is looked at when
      // somebody on site asks for a ticket, which is rare next to the other
      // three; it lives in More.
      ['/app', '/app/forms', '/app/library']
    : ['/app', '/app/calendar', '/app/jobs'];
}

/**
 * Everything that did not fit on the tab bar — the More page's list.
 *
 * `platformAdmin` is a second argument rather than a fourth role, because the
 * owner console's flag rides on the ACCOUNT, not the role — `isPlatformAdmin`
 * is false for every customer forever. Round 32 drew the console in the
 * desktop sidebar only, so on a phone (no sidebar, fixed tab slots) it was
 * unreachable — the same missing-door shape as the system feed below it
 * (found by Nathan on his own phone, 2026-09-17). Never an access control:
 * the server answers 404 to anyone without the flag, and requires TOTP
 * besides.
 */
export function moreDestinations(
  role: Role,
  platformAdmin = false,
  features: NavFeatures = {}
): NavItem[] {
  const tabbed = phoneTabTargets(role, features);
  const items = mainNav(role, features).filter(
    (item) => (!item.roles || item.roles.includes(role)) && !tabbed.includes(item.to)
  );
  // The system feed, for the roles that have one. It lives outside `mainNav`
  // (see its comment), so without this line a phone offers it nowhere.
  const feed = NOTIFICATIONS_NAV.roles!.includes(role) ? [NOTIFICATIONS_NAV] : [];
  // Settings for everyone — the page's own tabs are role-filtered, so a field
  // worker's Settings is just Security, which is exactly what their More page
  // offered before round 30 folded the loose links into one entry.
  //
  // The owner console LAST, below Settings — the sidebar's own order.
  return [
    ...items,
    ...feed,
    SETTINGS_NAV,
    ...(platformAdmin ? [PLATFORM_NAV] : []),
  ];
}
